import { useState, useEffect } from "react";
import {
  getClassTasks,
  deleteClassTask,
  createClassTask,
  updateClassTask,
} from "../../../../services/classTaskService";
import { ClassForTeacher, ClassTask, ClassTaskFilters, ClassWithSubjects } from "../../../../types/classTaskTypes";
import { message } from "antd";
import { fetchClassSubjects } from "../../../../services/classSubjectService";
import { getSubjectLabelFromCode } from "utils/schoolUtils";

export const useTasks = (initialFilters: ClassTaskFilters) => {
  const [tasks, setTasks] = useState<ClassTask[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<ClassTaskFilters>(initialFilters);
  const [subjects, setSubjects] = useState<{ value: string; label: string }[]>([]);
  const [classData, setClassData] = useState<ClassWithSubjects[]>([]);
  // Fetch tasks based on current filters
  const fetchTasks = async (overrideFilters?: Partial<ClassTaskFilters>) => {
    const activeFilters = { ...filters, ...overrideFilters };

    if (!activeFilters.teacherId && !activeFilters.studentId) {
      // Neither teacher nor student context — skip fetching
      setLoading(false);
      setTasks([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const activeFilters = { ...filters, ...overrideFilters }; // Merge filters
      const response = await getClassTasks(activeFilters);
      setTasks(response.data || []);
    } catch (err) {
      console.error("Error fetching tasks:", err);
      setError("Failed to fetch tasks.");
      message.error("Failed to fetch tasks.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch tasks whenever filters change
  useEffect(() => {
    fetchTasks();
  }, [filters]);

  // Function to delete a task
  const deleteTask = async (taskId: string) => {
    try {
      await deleteClassTask(taskId);
      setTasks((prevTasks) => prevTasks.filter((task) => task.id !== taskId));
      message.success("Task deleted successfully.");
    } catch (err) {
      message.error("Failed to delete task.");
    }
  };

  // Function to create a task
  const createTask = async (newTask: ClassTask) => {
    try {
      const createdTask = await createClassTask(newTask);
      setTasks((prevTasks) => [...prevTasks, createdTask]);
      message.success("Task created successfully.");
    } catch (err) {
      message.error("Failed to create task.");
    }
  };

  // Function to update a task
  const updateTask = async (taskId: string, updatedFields: Partial<ClassTask>) => {
    try {
      await updateClassTask(taskId, updatedFields);
      setTasks((prevTasks) =>
        prevTasks.map((task) =>
          task.id === taskId ? { ...task, ...updatedFields } : task
        )
      );
      message.success("Task updated successfully.");
    } catch (err) {
      message.error("Failed to update task.");
    }
  };

  // Fetch classes for a teacher
  const fetchClasses = async (teacherId: string) => {
    try {
      const response = await fetchClassSubjects({ teacherIds: [teacherId] });
  
      if (Array.isArray(response) && response.length > 0 && "teacherId" in response[0]) {
        const typedResponse = response as ClassForTeacher[];
        const allClasses = typedResponse[0].classes;
  
        setClassData(allClasses);
  
        // Extract unique subjects from all class.subjects
        const subjectMap = new Map<string, { name: string; code: string }>();
        console.log('cls.subjects',allClasses)

        allClasses.forEach((cls) => {
          cls.subjects.forEach(({ subjectId, subjectName, subjectCode }) => {
            if (!subjectMap.has(subjectId)) {
              subjectMap.set(subjectId, { name: subjectName, code: subjectCode ?? "" });
            }
          });
        });
        
        setSubjects(
          Array.from(subjectMap.entries()).map(([id, { name, code }]) => ({
            value: id,
            label: getSubjectLabelFromCode(code) || name, // fallback to name if code missing
          }))
        );
      } else {
        throw new Error("Unexpected response format.");
      }
    } catch (err) {
      console.error("Error fetching classes:", err);
      message.error("Failed to fetch classes.");
    }
  };
  

  return {
    tasks,
    classData,
    subjects,
    loading,
    error,
    filters,
    setFilters, // Allow dynamic filter updates
    fetchTasks,
    setTasks, // Expose setTasks for manual updates if necessary
    fetchClasses,
    deleteTask,
    createTask,
    updateTask,
  };
};
